import { useState, useEffect } from "react";

/**
 * Hook that returns true when the user is within a specified distance from the top of the page.
 * Uses requestAnimationFrame for optimal performance and throttling.
 *
 * @param threshold Distance from top in pixels (default: 20)
 * @returns boolean indicating if user is near the top
 */
export function useNearTop(threshold: number = 30): boolean {
    const [isNearTop, setIsNearTop] = useState(true);

    useEffect(() => {
        // Early return for SSR
        if (typeof window === "undefined") return;

        let ticking = false;

        const handleScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    const scrollY = window.scrollY || window.pageYOffset;
                    const nearTop = scrollY <= threshold;

                    setIsNearTop(nearTop);
                    ticking = false;
                });
                ticking = true;
            }
        };

        // Set initial state
        const initialScrollY = window.scrollY || window.pageYOffset;
        setIsNearTop(initialScrollY <= threshold);

        // Use passive listener for better performance
        window.addEventListener("scroll", handleScroll, { passive: true });

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [threshold]);

    return isNearTop;
}
